import {z} from 'zod';
import {zDateTime, zNumberToString} from '@brunata-angular/util';
import {DateTime} from 'luxon';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {FormValue} from '@brunata-angular/util';

export const reportingConsumerAmountSchema = z.object({
    amount: z.number().nullable(),
    accountNo: z.number(),
});

export const reportingConsumerSchema = z.object({
    moveCountry: z.string().nullable(),
    changed: z.boolean(),
    consumerNo: z.string().nullable(),
    moveAddress: z.string().nullable(),
    movePostalCode: z.string().nullable(),
    moveCity: z.string().nullable(),
    vatState: z.string().nullable(),
    occupationDate: zDateTime.nullable(),
    wbConsumerId: zNumberToString.nullable(),
    consumerId: zNumberToString.nullable(),
    name: z.string().nullable(),
    consumerAmounts: z.array(reportingConsumerAmountSchema),
});

export const reportingLocationSchema = z.object({
    wbLocationId: zNumberToString,
    locationNo: z.string().nullable(),
    secondSectorNo: z.string().nullable(),
    firstSectorNo: z.string().nullable(),
    street: z.string().nullable(),
    stairway: z.string().nullable(),
    floor: z.string().nullable(),
    country: z.string(),
    postalCode: z.string(),
    dismantleDate: zDateTime.nullable(),
    futureOccupationDate: zDateTime.nullable(),
    consumers: z.array(reportingConsumerSchema),
});

export type ReportingLocation = z.infer<typeof reportingLocationSchema>;
export type ReportingConsumer = z.infer<typeof reportingConsumerSchema>;

export function locationToApiModel(
    location: FormValue<LocationControl>,
): UpdateReportingLocationRequestBody {
    return {
        consumers: location.consumers.map(consumer => ({
            consumerId: consumer.consumerId,
            moveCountry: consumer.moveCountry.toUpperCase(),
            consumerNo: consumer.consumerNo,
            moveAddress: consumer.moveAddress,
            movePostalCode: consumer.movePostalCode,
            moveCity: consumer.moveCity,
            vatState: consumer.vatState,
            occupationDate: consumer.occupationDate?.toISODate() ?? null,
            wbConsumerId: consumer.wbConsumerId,
            name: consumer.name,
            consumerAmounts: consumer.consumerAmounts.map(consumerAmount => ({
                amount: consumerAmount.amount,
                accountNo: consumerAmount.accountNo.toString(10),
            })),
        })),
    };
}

export type UpdateReportingLocationRequestBody = {
    consumers: {
        consumerId: string | null;
        moveCountry: string | null;
        moveCity: string | null;
        consumerNo: string;
        moveAddress: string | null;
        movePostalCode: string | null;
        vatState: string;
        occupationDate: string | null;
        wbConsumerId: string | null;
        name: string;
        consumerAmounts: {
            amount: number;
            accountNo: string;
        }[];
    }[];
};

export type LocationControl = FormGroup<{
    wbLocationId: FormControl<string>;
    secondSectorNo: FormControl<string | null>;
    firstSectorNo: FormControl<string | null>;
    locationNo: FormControl<string | null>;
    street: FormControl<string | null>;
    stairway: FormControl<string | null>;
    floor: FormControl<string | null>;
    country: FormControl<string>;
    dismantleDate: FormControl<DateTime | null>;
    futureOccupationDate: FormControl<DateTime | null>;
    consumers: FormArray<ConsumerControl>;
}>;

export type ConsumerControl = FormGroup<{
    consumerAmounts: FormArray<ConsumerAmountControl>;
    consumerId: FormControl<string | null>;
    consumerNo: FormControl<string>;
    moveAddress: FormControl<string>;
    moveCountry: FormControl<string>;
    movePostalCode: FormControl<string>;
    moveCity: FormControl<string>;
    name: FormControl<string>;
    occupationDate: FormControl<DateTime | null>;
    occupationEndDate: FormControl<DateTime | null>;
    vatState: FormControl<string>;
    wbConsumerId: FormControl<string | null>;
}>;

export type ConsumerAmountControl = FormGroup<{
    accountNo: FormControl<number>;
    amount: FormControl<number>;
}>;
